import { useEffect, useState, useContext, useMemo, RefObject } from "react";

import { VirtualArticleContext } from './VirtualArticleContext'

export function useShowComponent(devices: Devices): string {
    const [classNames, setClassNames] = useState<Array<string>>([]);
    useEffect(() => {
        if (!devices) {
            return;
        }
        const response: Array<string> = [];
        if (devices.phone === 'off') {
            response.push('hide-mobile')
        }

        if (devices.tablet === 'off') {
            response.push('hide-tablet')
        }

        if (devices.desktop === 'off') {
            response.push('hide-desktop')
        }
        setClassNames(response);
    }, [devices])


    return classNames.join(' ');
}

export interface Devices {
    phone: OnOff;
    tablet: OnOff;
    desktop: OnOff;
}

export type OnOff = 'on' | 'off';

/**
 * React hook that process animations
 */
export function useComponentAnimation({
    ref,
    animation
}: ComponentAnimationArgs): Array<string> {
    const [classNames, setClassNames] = useState<Array<string>>([]);
    const hasAnimation = animation && animation.type !== 'none';
    const isInViewport = useIsInViewport(ref, hasAnimation);

    const { isSelected } = useContext(VirtualArticleContext);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (!ref) {
            return;
        }
        if (isInViewport) {
            setIsLoaded(true);
        }
    }, [isInViewport, ref]);

    useEffect(() => {
        if (!animation || !isSelected || !isLoaded || !hasAnimation) {
            setClassNames([]);
            return;
        }

        const { type, params } = animation;
        const classNames = [
            ...getAnimationName(type),
            ...getAnimationParams(ref.current, params),
        ];
        setClassNames(classNames);
    }, [isLoaded, isSelected]);

    return classNames;
}

/**
 * React hook that checks if the component is in the viewport
 */
export function useIsInViewport(ref: RefObject<any>, hasAnimation: boolean) {
    const [isIntersecting, setIsIntersecting] = useState(false);

    const observer = useMemo(
        () =>
            hasAnimation
                ? new IntersectionObserver(([entry]) => {
                    setIsIntersecting(entry.isIntersecting);
                })
                : null,
        [hasAnimation, ref],
    );

    useEffect(() => {
        if (observer) {
            if (ref?.current instanceof Element) {
                observer.observe(ref.current);
            }
        }
        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [ref, observer, hasAnimation]);

    return isIntersecting;
}


interface ComponentAnimationArgs {
    ref: React.MutableRefObject<any>,
    animation: any;
}

function getAnimationName(type: string) {
    const classNames = ['animate__animated'];
    classNames.push(`animate__${type}`);

    return classNames;
}

export function getAnimationParams(element: any, params: AnimationParams): Array<string> {
    const classNames: Array<string> = [];
    if (!params) {
        return classNames;
    }
    const { delay, speed } = params;
    let { repeat } = params;

    repeat = repeat === 'infinite' ? 'infinite' : parseInt(`${repeat}`);

    if (delay) {
        element.style.setProperty('--animate-delay', `${delay}s`);
        classNames.push(`animate__delay-1s`);
    }
    if (repeat) {
        element.style.setProperty('--animate-repeat', `${repeat}`);
        classNames.push(`animate__repeat-1`);
    }
    if (speed) {
        classNames.push(`animate__${speed}`);
    }
    return classNames;
}

interface AnimationParams {
    delay: number,
    repeat: string | number;
    speed: any;
}